"use client";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { heroBackgrounds } from "@/lib/backgrounds";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSyncExternalStore } from "react";
import CommonImage from "../commonImage/commonImage.component";
import {
  CategoriesMenuPanel,
  MobileNavDrawer,
  ProductsMenuPanel,
} from "./components";
import { headerCta, isNavDropdown, navItems, panelRoutes } from "./data";
import { useIsScrolledPast } from "./hooks";

const subscribeNoop = () => () => {};

const navItemBase =
  "h-auto rounded-[10px] bg-transparent px-3.5 py-2.5 text-base font-medium transition-colors";

// Top-level item colours for the two header states: over a coloured hero
// (white text, translucent white hover) and the solid white bar.
const navItemTone = {
  hero: "text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white data-[state=open]:bg-white/15 data-[state=open]:text-white data-[state=open]:hover:bg-white/15 data-[state=open]:focus:bg-white/15 data-[active=true]:bg-white/15 data-[active=true]:text-white",
  solid:
    "text-ink hover:bg-paper hover:text-ink focus:bg-paper focus:text-ink data-[state=open]:bg-paper data-[state=open]:text-ink data-[state=open]:hover:bg-paper data-[state=open]:focus:bg-paper data-[active=true]:bg-paper data-[active=true]:text-primary",
};

// Website 2.0 header: mega menus from lg up and the MobileNavDrawer
// hamburger below that (mobile-first: unprefixed classes are the phone
// layout, `lg:` switches the desktop nav on).
const HeaderSection = () => {
  const isScrolledPast = useIsScrolledPast(1100);
  const pathname = usePathname();

  // useIsScrolledPast reads window.scrollY on its first render, which the
  // server can't know - gating on isClient keeps hydration identical to the
  // server HTML. useSyncExternalStore returns the server snapshot (false)
  // while hydrating and true afterwards, with no setState-in-effect.
  const isClient = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );

  const bg = pathname.startsWith("/blogs/")
    ? heroBackgrounds["/blogs/slug"]
    : pathname.startsWith("/checkout/")
      ? heroBackgrounds["/checkout"]
      : heroBackgrounds[pathname] || "bg-transparent";

  // Any page with a coloured hero (see src/lib/backgrounds.ts) gets the same
  // scroll effect: the header blends into the hero colour until the visitor
  // scrolls past it, then becomes the solid white 2.0 bar with a shadow.
  const isSticky = isClient && isScrolledPast;
  const isOnHero = bg !== "default" && !isSticky;
  const tone = isOnHero ? navItemTone.hero : navItemTone.solid;

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b transition-[background-color,border-color,box-shadow,color] duration-300",
        isOnHero
          ? `${bg} border-transparent text-white`
          : "border-line bg-white text-ink",
        isSticky && "shadow-md",
      )}
    >
      <div className="scontainer">
        {/* `relative` here is what the full-width mega menu panels anchor
            to - see the NavigationMenu comment below. */}
        <div className="relative flex h-[72px] items-center gap-8">
          <Link href="/" className="shrink-0" aria-label="Fydo home">
            <CommonImage
              alt="Fydo Logo"
              src={isOnHero ? "/icons/FYDO.png" : "/icons/FYDO-red.png"}
              width={120}
              height={40}
              priority
              className="h-[24px] w-auto lg:h-[30px]"
            />
          </Link>

          {/* Root, list wrapper and items are all forced `static` so each
              dropdown panel positions against the row above and spans the
              full header width. Radix wraps the <ul> in a div with an inline
              `position: relative`, hence the `[&>div]:static!` override. */}
          <NavigationMenu
            viewport={false}
            className="static hidden max-w-none flex-1 justify-start lg:flex [&>div]:static!"
          >
            <NavigationMenuList className="gap-1">
              {navItems.map((item) => {
                if (!isNavDropdown(item)) {
                  const isCurrent = pathname === item.route;
                  return (
                    <NavigationMenuItem key={item.route} className="static">
                      <NavigationMenuLink
                        asChild
                        active={isCurrent}
                        className={cn(
                          navItemBase,
                          tone,
                          "data-[active=true]:font-semibold",
                        )}
                      >
                        <Link
                          href={item.route}
                          aria-current={isCurrent ? "page" : undefined}
                        >
                          {item.label}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  );
                }

                const isSectionActive = panelRoutes(item.panel).includes(
                  pathname,
                );
                return (
                  <NavigationMenuItem key={item.label} className="static">
                    <NavigationMenuTrigger
                      className={cn(
                        navItemBase,
                        tone,
                        isSectionActive &&
                          cn("font-semibold", !isOnHero && "text-primary"),
                      )}
                    >
                      {item.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent className="right-0 mt-px overflow-hidden rounded-2xl border-line bg-white p-0 pr-0 text-ink shadow-panel md:w-auto">
                      {item.panel.kind === "products" ? (
                        <ProductsMenuPanel
                          panel={item.panel}
                          pathname={pathname}
                        />
                      ) : (
                        <CategoriesMenuPanel
                          panel={item.panel}
                          pathname={pathname}
                          idPrefix={`nav-${item.label.toLowerCase()}`}
                        />
                      )}
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              })}
            </NavigationMenuList>
          </NavigationMenu>

          {/* White pill over a coloured hero (a red pill would vanish into a
              bg-primary hero), the brand-red pill on the white bar. */}
          <Button
            asChild
            variant="brand"
            className={cn(
              "hidden h-auto rounded-full px-5 py-[11px] text-[15px] lg:ml-auto lg:inline-flex",
              isOnHero &&
                "bg-white text-primary hover:bg-white/90 hover:text-primary",
            )}
          >
            <Link href={headerCta.route}>{headerCta.label}</Link>
          </Button>

          <MobileNavDrawer pathname={pathname} isOnHero={isOnHero} />
        </div>
      </div>
    </header>
  );
};

export default HeaderSection;
